import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  useOrderedSections,
  useVisibility,
} from "@smart/bridge-intake-components-dom";
import { defaultClientRoleId } from "@smart/bridge-smokeball-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import { Alert, Modal } from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { fromEntries, jsonParseOrReturn } from "@smart/itops-utils-basic";
import {
  useCreateMatter,
  useDeleteSubmission,
  useLoadFields,
  useLoadGroups,
  useLoadMatterTypes,
  useLoadSections,
  useLoadSubmission,
} from "@smart/manage-gql-client-dom";

import { AddContactDetails, CreateContact } from "./add-contact-details";
import { PreviewResponses } from "./preview-responses";
import { findClientRoles } from "../../../shared/validation-utils";

type NoContactModalProps = {
  onClose: () => void;
  formUri: string;
  submissionUri: string;
  isRefetching: boolean;
};

export const NoContactModal = ({
  formUri,
  submissionUri,
  onClose,
  isRefetching,
}: NoContactModalProps) => {
  const [deleteSubmission, { loading: deletingSubmission }] =
    useDeleteSubmission();
  const [createMatter, { loading: creatingLead }] = useCreateMatter();
  const { host } = useSmokeballApp();
  const { terms } = loadLocale();
  const [contactType, setContactType] = useState<"person" | "company">(
    "person",
  );

  const submission = useLoadSubmission({
    uri: submissionUri,
  });

  const { result: matterTypesResult, loading: loadingMatterTypes } =
    useLoadMatterTypes(
      {
        matterTypeIds:
          submission.result?.form?.values?.matterTypes?.map((mt) => mt.id) ||
          [],
        type: submission.result?.form?.values?.category!,
      },
      { skip: !submission.result?.form?.values?.category },
    );

  const { result: sectionsResult, loading: loadingSections } = useLoadSections({
    formUri,
    submissionUri,
  });
  const { result: groupsResult, loading: loadingGroups } = useLoadGroups({
    formUri,
    submissionUri,
  });
  const { result: fieldsResult, loading: loadingFields } = useLoadFields({
    formUri,
    submissionUri,
  });

  const groups = groupsResult?.map((g) => g.values) || [];
  const sections = sectionsResult?.map((s) => s.values) || [];
  const fields = fieldsResult?.map((f) => f.values) || [];
  const responses = fromEntries(
    (submission?.result?.responses || []).map((r) => [
      r.fieldUri,
      jsonParseOrReturn(r.value),
    ]),
  );

  const visibility = useVisibility({
    groups,
    sections,
    fields,
    responses,
  });

  const orderedSections = useOrderedSections({
    groups,
    sections,
    fields,
    visibility,
    responses,
  });
  const personSchema = z.object({
    firstName: z.string().min(1, "Please enter a first name"),
    lastName: z.string().min(1, "Please enter a last name"),
    contactId: z.string().optional(),
  });
  const companySchema = z.object({
    companyName: z.string().min(1, "Please enter a company name"),
    contactId: z.string().optional(),
  });
  const addContactDetailsFormSchema = useMemo(
    () => (contactType === "person" ? personSchema : companySchema),
    [contactType],
  );

  const { control, handleSubmit, setValue, trigger, watch } =
    useForm<CreateContact>({
      defaultValues: {
        firstName: "",
        lastName: "",
        companyName: "",
        contactId: undefined,
      },
      resolver: zodResolver(addContactDetailsFormSchema),
    });

  const onDeleteSubmission = async () => {
    await deleteSubmission({ variables: { uri: submissionUri } });
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!submission.result) return;
    try {
      await createMatter({
        variables: {
          submissionUri,
          person:
            contactType === "person"
              ? {
                  firstName: values.firstName,
                  lastName: values.lastName,
                  contactId: values.contactId,
                }
              : undefined,
          company:
            contactType === "company"
              ? {
                  companyName: values.companyName,
                  contactId: values.contactId,
                }
              : undefined,
        },
      });
      onClose();
    } catch (e) {
      console.error(e);
    }
  });

  const availableContactTypes = useMemo(() => {
    const clientRoles = [
      ...new Set(findClientRoles(matterTypesResult)),
      defaultClientRoleId,
    ];

    return fieldsResult?.reduce(
      (acc, f) => {
        const isClientField =
          f.values.layout?.id && clientRoles.includes(f.values.layout.id);
        if (!isClientField) return acc;
        if (
          f.values.field?.name?.startsWith("person/") &&
          !acc.includes("person")
        ) {
          return [...acc, "person" as const];
        }
        if (
          f.values.field?.name?.startsWith("company/") &&
          !acc.includes("company")
        ) {
          return [...acc, "company" as const];
        }
        return acc;
      },
      [] as ("person" | "company")[],
    );
  }, [fieldsResult, matterTypesResult]);

  useEffect(() => {
    if (!availableContactTypes?.length) {
      setContactType("person");
    } else {
      setContactType(availableContactTypes[0]);
    }
  }, [availableContactTypes]);

  const loadingSubmission =
    loadingSections || loadingGroups || loadingFields || loadingMatterTypes;

  const disableModal =
    deletingSubmission || creatingLead || loadingSubmission || isRefetching;

  return (
    <Modal
      open
      header={{
        icon: "solidTriangleExclamation",
        iconBgColor: "bg-yellow-450",
        text: "Add Contact Details to Create Lead",
      }}
      size="xLarge"
      onClose={onClose}
      loading={disableModal}
      variant="nonScrollable"
      footer={{
        buttons: [
          {
            text: `Delete ${terms.enquiry.title}`,
            type: "reset",
            variant: "destructiveSubtle",
            disabled: disableModal,
            onClick: onDeleteSubmission,
          },
          {
            text: "Cancel",
            type: "reset",
            variant: "secondarySubtle",
            disabled: disableModal,
            onClick: onClose,
          },
          {
            text: "Create Lead",
            variant: "primaryDefault",
            disabled: disableModal,
            onClick: onSubmit,
          },
        ],
      }}
    >
      <div className="flex h-[58.3rem] overflow-y-hidden">
        <div className="flex flex-col flex-1 pr-[2.4rem] border-r border-neutral-100 gap-large">
          <p>
            This lead was not created because a contact name was not entered.
            You can manually enter them now to create the lead.
          </p>
          <PreviewResponses
            orderedSections={orderedSections}
            responses={responses}
            loading={loadingSubmission}
          />
        </div>
        <div className="flex-1 pl-[2.4rem] overflow-auto">
          <AddContactDetails
            loading={loadingSubmission}
            control={control}
            setValue={setValue}
            trigger={trigger}
            isContactSelected={!!watch("contactId")}
            showContactTypeSelect={
              !!availableContactTypes?.length &&
              availableContactTypes.length > 1
            }
            contactType={contactType}
            setContactType={setContactType}
          />
          <div className="mt-[3.2rem]">
            <Alert
              variant="section"
              actions={[
                {
                  text: "Open Form Builder",
                  onClick: () =>
                    host?.navigate?.({ target: "intake-form-builder" }),
                },
              ]}
              content="Review your form configuration to ensure all mandatory fields are included."
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
